import {TaggedLogger} from "@nextlot/core/utilities";
import NetworkDisconnectedAlertPanelOverlayCmp from "../NetworkDisconnectedAlertPanelOverlayCmp";
import WebcastPageFooterLineCmp from "../WebcastPageFooterLineCmp";
import useWebcastConnect from "@nextlot/core/webcast/hooks/useWebcastConnect";
import {
    ROUND_STOP_TYPES_MAP,
    ROUND_TYPES_DISPLAY_NAMES_MAP,
    RoundStopTypeEnum, RoundTypeEnum,
    WebcastStateEnum
} from "@nextlot/core/webcast/data/WebcastDataDomain";
import React from "react";
import {connect} from "react-redux";
import {ReduxStateType} from "@nextlot/core/webcast/services/DataStore";
import {Bid, Lot, Participant, Round} from "@nextlot/core/webcast/data/data_types_definitions";
import ClerkCurrentRoundPrebidsCmp from "../clerk/ClerkCurrentRoundHighestOnlineBidsCmp";
import {ReduxWebcastCurrentRoundType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastCurrentRound";
import {CurrencyAmountDisplay} from "../CurrencyAmountDisplay";
import {ReduxWebcastCurrentRoundBiddingType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastCurrentRoundBidding";
import AutoSizer from "react-virtualized-auto-sizer";
import {VariableSizeList, areEqual} from "react-window";
import {useWindowResize} from '../../hooks/useWindowResize';
import memoizeOne from "memoize-one";
import {ReduxWebcastAuctionDataType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastAuctionData";
import NumberDisplay from "../clerk/NumberDisplay";


type RootCmpPropsType = {
    webcastAuctionData: ReduxWebcastAuctionDataType,
    webcastStatusState: number,
    currentRound: ReduxWebcastCurrentRoundType,
    currentRoundBidding: ReduxWebcastCurrentRoundBiddingType,
    lotsMapById: Map<number, Lot>,
    highestBid: Bid,
    participantsByUserId: Map<number, Participant>,
    bidderPersonName: string,
};


type LotListItemDataType = {
    closedRounds: Array<Round>,
    lotsMapById: Map<number, Lot>
}


type LotListItemPropsType = {
    data: LotListItemDataType,
    index: number,
    style: {},
    setSize: (index: any, size: any) => void;
    windowWidth: number;
};

const _logger = TaggedLogger.get('AuctioneerRootCmp');

function AuctioneerRootCmp(props: RootCmpPropsType) {
    _logger.debug('.render()', props);

    const webcastStatusState = useWebcastConnect();


    return (
        <>
            <NetworkDisconnectedAlertPanelOverlayCmp/>

            {

                (webcastStatusState === WebcastStateEnum.WEBCAST_STATE__CLOSED)
                    ? <FinishedAppStateViewCmp/>
                    : (webcastStatusState > WebcastStateEnum.WEBCAST_STATE__LOADING)
                        ? <LoadedAppStateViewCmp {...props} />
                        : <LoadingAppStateViewCmp/>

            }

            <WebcastPageFooterLineCmp screenName={'Auctioneer'}/>
        </>
    )
}

function FinishedAppStateViewCmp() {
    return (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <h3>The webcast event has closed. Thank you.</h3>
        </div>
    )
}

function LoadingAppStateViewCmp() {
    return (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <h3>Loading, please wait ...</h3>
        </div>
    )
}

function LoadedAppStateViewCmp(props: { webcastAuctionData: ReduxWebcastAuctionDataType, currentRound: ReduxWebcastCurrentRoundType, currentRoundBidding: ReduxWebcastCurrentRoundBiddingType, highestBid: Bid, participantsByUserId: Map<number, Participant>}) {

    const {webcastAuctionData, currentRound, currentRoundBidding, highestBid} = props;
    const lotsMapById = webcastAuctionData.lotsMapById;
    const lots = currentRound.lotsIds.map(lotId => lotsMapById.get(lotId));
    const closedRounds = webcastAuctionData.closedRounds;
    const highestBidParticipant: Participant = props.highestBid && props.participantsByUserId.get(props.highestBid.userId);
    const highestBidParticipantPersonName:string = highestBidParticipant ? highestBidParticipant.bidderPersonName : null;
    const isInternetBidder:boolean = highestBid && highestBid.bidderDisplay !== "FLOOR";
    const [animate, setAnimate] = React.useState(false);

    const lotsListRef = React.useRef(null);

    let mostRecentClosedRound = closedRounds.map((closedRound, index) => ({
        ...closedRound,
        index
    })).sort((a, b) => a.index > b.index ? -1 : 1);

    const itemData = createLotsListItemData(mostRecentClosedRound, lotsMapById);

    // VariableSizeList item size processing
    const lotListItemSizeMap = React.useRef({});
    const setLotListItemSize = React.useCallback((index, size) => {
      lotListItemSizeMap.current = { ...lotListItemSizeMap.current, [index]: size };
      lotsListRef.current.resetAfterIndex(index);
    }, []);
    const getLotListItemSize = (index: number) => lotListItemSizeMap.current[index] + 13 || 55; // 13px adjustment for bottom spacing;
    const [windowWidth] = useWindowResize();

    // keep in sync with $webcast-flash-colors in file _auctioneer_variables.scss
    const flashColors = ['salmon', 'orange', 'yellow', 'lime', 'skyblue', 'mediumpurple']
    const [flashColorIndex, setFlashColorIndex] = React.useState(-1);

    React.useEffect(() => {
      if (isInternetBidder) {
          setAnimate(false); // Reset the animation

          setFlashColorIndex(flashColorIndex < 0 ? 0 : flashColorIndex == 5 ? 0 : flashColorIndex + 1)


        // Ensuring the class name is first removed
        // by waiting a frame to pass and the DOM to be updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setAnimate(true); // Reapply the animation
          });
        });
      }
    }, [highestBid]);

    return (
        <div className="d-flex flex-column flex-grow-1">
            <div
                className={`auctioneer-content-wrapper row mb-3 mx-1 py-2 flex-grow-1 ${isInternetBidder && animate ? `flash-${flashColors[flashColorIndex]}` : ''}`}>
                <div className="col-3">
                    <div className="d-flex align-content-around flex-column">
                        <div className="mb-5">
                            {lots[0].images && lots[0].images.length > 0 &&
                            <img className="img-fluid" src={lots[0].images[0].mdUrl}/>}
                        </div>
                    </div>
                </div>
                <div className="col-9">
                    <div className="d-flex flex-column flex-grow-1">
                        <p className="h3 text-end">Asking Price: {currentRoundBidding.askingBidValue
                            ? <CurrencyAmountDisplay valueCents={currentRoundBidding.askingBidValue}/>
                            : '0'
                        }
                        </p>
                        <BidDetails currentRound={currentRound} currentRoundBidding={currentRoundBidding}
                                    bidderPersonName={highestBidParticipantPersonName} currentLot={lots[0]}/>
                        <div className="row">
                            <div className="col-10 col-md-6 col-xl-4"><ClerkCurrentRoundPrebidsCmp/></div>
                        </div>
                    </div>
                </div>
                <div className="col-6 d-flex">
                    <div className="card mt-1 flex-grow-1">
                        <div className="card-header bg-light">
                            Bidding on
                        </div>
                        <div className="card-body position-relative overflow-auto">
                            <div className="row">
                                <div className="col-12">
                                    <p className="h3">{ROUND_TYPES_DISPLAY_NAMES_MAP.get(currentRound.roundType)}</p>
                                    <p className="ps-2 h5">
                                        {lots.map(lot => <span key={lot.id}>Lot #{lot.number}<br/></span>)}
                                    </p>
                                    <p className="ps-2 h4">
                                        {lots.map(lot => <span key={lot.id}>{lot.name}<br/></span>)}
                                    </p>
                                    <div className="ps-2 mb-2 text-truncate-two">
                                        {lots.map(lot => <span key={lot.id}> <div dangerouslySetInnerHTML={{__html: lot.description}}/> <br/></span>)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-6 d-flex">
                    <div className="card mt-1 flex-grow-1">
                        <div className="card-header bg-light">
                            Closed Lots
                        </div>
                        <div className="card-body d-flex">
                            <div className="d-flex flex-grow-1">
                                <div className="d-flex flex-grow-1">
                                    <AutoSizer>
                                        {({height, width}) => (
                                            <VariableSizeList
                                                ref={lotsListRef}
                                                className="auctioneer-lots-list__list-container"
                                                height={height}
                                                width={width}
                                                itemCount={closedRounds.length}
                                                itemSize={getLotListItemSize}
                                                itemData={itemData}
                                                itemKey={(index, itemData) => (itemData.closedRounds[index].roundId)}
                                            >
                                                {({ data, index, style }) => (
                                                  <LotListItem
                                                      data={data}
                                                      index={index}
                                                      style={style}
                                                      setSize={setLotListItemSize}
                                                      windowWidth={windowWidth}
                                                  />
                                                )}
                                            </VariableSizeList>
                                        )}
                                    </AutoSizer>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}


function BidDetails(props: { currentRound: ReduxWebcastCurrentRoundType, currentRoundBidding: ReduxWebcastCurrentRoundBiddingType, bidderPersonName: string, currentLot: Lot }) {
    const {currentRound, currentRoundBidding, bidderPersonName, currentLot} = props;
    const {highestBid} = props.currentRoundBidding;

    if (currentRound.stopType === RoundStopTypeEnum.CANCEL) {
        return <h3 className="display-2">Cancel</h3>
    } else if (currentRound.stopType === RoundStopTypeEnum.NO_SALE) {
        return <h3 className="display-2">No Sale</h3>
    } else if (highestBid) {
        return (
            <div className="auctioneer-bid-details d-flex flex-column flex-grow-1 align-items-left">
                <div className="mb-5 mb-3">
                    <h1 className={`mb-3 fw-bold text-break ${highestBid.bidderDisplay === "FLOOR" ? 'floor-bid' : 'online-bid'}`}
                        style={{fontSize: '7vw'}}
                    >
                        {highestBid.bidderDisplay}
                    </h1>

                    <h1 className="text-body-secondary text-break fw-bold"
                        style={{fontSize: '4vw'}}
                    >{bidderPersonName}</h1>

                    <h1 className="pt-3 display-1 fw-bold">
                        <CurrencyAmountDisplay
                            className={currentRound.stopType === RoundStopTypeEnum.SOLD ? 'flash' : ''}
                            valueCents={highestBid.value}/>
                    </h1>
                    {
                        currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT &&
                        <h1 className="fw-bold">
                            <span> x {currentLot.itemsQty} = <CurrencyAmountDisplay valueCents={highestBid.value * currentLot.itemsQty}/></span>
                        </h1>
                    }



                </div>
                <div className="mt-5">
                    {currentRound.stopType === RoundStopTypeEnum.PENDING
                        ? (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE || currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN)
                            ? ((currentRoundBidding.choiceLotsSelection && currentRoundBidding.choiceLotsSelection.length)
                                    ? <h3 className="display-2">Pending</h3>
                                    : ''
                            )
                            : <h3 className="display-2">Pending</h3>
                        : ''
                    }
                    {currentRound.stopType === RoundStopTypeEnum.SOLD
                        ? (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE || currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN)
                            ? ((currentRoundBidding.choiceLotsSelection && currentRoundBidding.choiceLotsSelection.length)
                                    ? <h3 className="display-2">Sold</h3>
                                    : ''
                            )

                            : <h3 className="display-2">Sold</h3>
                        : ''
                    }
                </div>
            </div>
        )
    }


    return <h1 className="display-1 text-start">Waiting for bidding to start</h1>


}

// React.memo acts as a PureComponent here (https://reactjs.org/blog/2018/10/23/react-v-16-6.html#reactmemo)
const LotListItem = React.memo(function LotListItemMemoCmp(props: LotListItemPropsType) {
    const {index, style, setSize, windowWidth} = props;
    const itemData: LotListItemDataType = props.data;
    const closedRounds = itemData.closedRounds;
    const lotsMapById = itemData.lotsMapById;
    const round: Round = closedRounds[index];
    const itemsQty = round.lotsIds.map(lotId => lotsMapById.get(lotId).itemsQty);
    const totalPrice = round.highestBid ? round.highestBid.value * itemsQty[0] : null;

    const rowRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      setSize(index, rowRef.current.getBoundingClientRect().height);
    }, [setSize, index, windowWidth]);

    return (
        <div className="round-list__item" style={style}>
            <div ref={rowRef}>
              <p className="mb-0 fw-bold">{ROUND_TYPES_DISPLAY_NAMES_MAP.get(round.roundType)}: {round.lotsIds.map(lotId => `#${lotsMapById.get(lotId).number}`).join(", ")}</p>
              {round.stopType !== RoundStopTypeEnum.NO_SALE ?
                  <p>
                      <strong>{ROUND_STOP_TYPES_MAP.get(round.stopType)}</strong> for <strong><NumberDisplay
                      valueCents={round.highestBid.value} allowZero={false}/>
                      {round.roundType === RoundTypeEnum.GROUP_CHOICE ||
                      round.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN ||
                      round.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY ?
                          ' each'
                          :
                          ''
                      }
                      {round.roundType === RoundTypeEnum.SINGLE_BOXLOT && itemsQty[0] > 1 ?
                          (<span> X {itemsQty} = <NumberDisplay valueCents={totalPrice} allowZero={false}/></span>)
                          :
                          ''
                      }
                  </strong> to <span
                      className="mb-0 text-danger fw-bold">{round.highestBid.bidderNo === '0' ? 'FLOOR' : `${round.highestBid.bidderNo} (${round.highestBid.bidderLabel})`}</span>
                  </p>
                  :
                  <p className="text-danger fw-bold">NO SALE</p>
              }
            </div>
        </div>
    )
}, areEqual);

const createLotsListItemData = memoizeOne((closedRounds, lotsMapById) => ({
    closedRounds,
    lotsMapById
}));

export default connect(({activeWebcast}: ReduxStateType) => {
    return {
        webcastAuctionData: activeWebcast.webcastAuctionData,
        currentRound: activeWebcast.webcastCurrentRound,
        currentRoundBidding: activeWebcast.webcastCurrentRoundBidding,
        webcastStatusState: activeWebcast.webcastStatus.state,
        highestBid: activeWebcast.webcastCurrentRoundBidding.highestBid,
        participantsByUserId: activeWebcast.webcastParticipants.mapByUserId,
    }
})(AuctioneerRootCmp);

// import {connect} from "react-redux";
// import {TaggedLogger} from "@nextlot/bidding-jsclient-core/lib/util";
// import type {ReduxActiveWebcastSubStateType} from "@nextlot/bidding-jsclient-core/lib/redux/reducerActiveWebcast";
// import WebcastPageFooterLineCmp from "../WebcastPageFooterLineCmp";
// import type {ReduxWebcastCurrentRoundType} from "@nextlot/bidding-jsclient-core/lib/redux/webcast/reducerWebcastCurrentRound";
// import {
//     ROUND_STOP_TYPES_MAP,
//     ROUND_TYPES_DISPLAY_NAMES_MAP,RoundStopTypeEnum,
//     RoundTypeEnum,
//     WebcastStateEnum
// } from "@nextlot/bidding-jsclient-core/lib/data/WebcastDataDomain";
// import AuctionsManager from "@nextlot/bidding-jsclient-core/lib/services/AuctionsManager";
// import NetworkDisconnectedAlertPanelOverlayCmp from "../NetworkDisconnectedAlertPanelOverlayCmp";
// import type {Bid, Lot, Participant, Round} from "@nextlot/bidding-jsclient-core/lib/data/data_types_definitions";
// import type {ReduxWebcastAuctionDataType} from "@nextlot/bidding-jsclient-core/lib/redux/webcast/reducerWebcastAuctionData";
// import type {ReduxWebcastCurrentRoundBiddingType} from "@nextlot/bidding-jsclient-core/lib/redux/webcast/reducerWebcastCurrentRoundBidding";
// import {CurrencyAmountDisplay} from "../CurrencyAmountDisplay";
// import AutoSizer from "react-virtualized-auto-sizer";
// import {areEqual, FixedSizeList} from "react-window";
// import memoizeOne from "memoize-one";
// import NumberDisplay from "../clerk/NumberDisplay";
// import ClerkCurrentRoundPrebidsCmp from "../clerk/ClerkCurrentRoundPrebidsCmp";
//
//
// type RootCmpPropsType = {
//     webcastAuctionData: ReduxWebcastAuctionDataType,
//     webcastStatusState: number,
//     currentRound: ReduxWebcastCurrentRoundType,
//     currentRoundBidding: ReduxWebcastCurrentRoundBiddingType,
//     lotsMapById: Map<number, Lot>,
//     highestBid: Bid,
//     participantsByUserId:Map,
//     bidderPersonName: string,
// };
//
// type RootCmpStateType = {
//     connectInProgress: boolean,
// };
//
// type LotListItemDataType = {
//     closedRounds: Array<Round>,
//     lotsMapById: Map<number, Lot>
// }
//
//
// type LotListItemPropsType = {
//     data: LotListItemDataType,
//     index: number,
//     style: {}
// };
//
// const _logger = TaggedLogger.get('AuctioneerRootCmp');
//
//
// function BidDetails(props: { currentRound: ReduxWebcastCurrentRoundType, currentRoundBidding: ReduxWebcastCurrentRoundBiddingType }) {
//     const {currentRound, currentRoundBidding, bidderPersonName} = props;
//     const {highestBid} = props.currentRoundBidding;
//
//     if (currentRound.stopType === RoundStopTypeEnum.CANCEL) {
//         return <h3 className="display-2">Cancel</h3>
//     } else if (currentRound.stopType === RoundStopTypeEnum.NO_SALE) {
//         return <h3 className="display-2">No Sale</h3>
//     } else if (highestBid) {
//         return (
//             <div className="auctioneer-bid-details d-flex flex-column flex-grow-1 align-items-left">
//                 <div className="mb-5 mb-3">
//                     <h1 className={`display-2 mb-3 fw-bold ${highestBid.bidderDisplay === "FLOOR" ? 'floor-bid' : 'online-bid'}`}>
//                         {highestBid.bidderDisplay}
//                     </h1>
//
//                     <h1 className="display-2 text-body-secondary fw-bold">{bidderPersonName}</h1>
//
//                     <h1 className="display-1 fw-bold">
//                         <CurrencyAmountDisplay
//                             className={currentRound.stopType === RoundStopTypeEnum.SOLD ? 'flash' : ''}
//                             valueCents={highestBid.value}/>
//                     </h1>
//                 </div>
//                 <div className="mt-5">
//                     {currentRound.stopType === RoundStopTypeEnum.PENDING
//                         ? (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE || currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN)
//                             ? ((currentRoundBidding.choiceLotsSelection && currentRoundBidding.choiceLotsSelection.length)
//                                     ? <h3 className="display-2">Pending</h3>
//                                     : ''
//                             )
//                             : <h3 className="display-2">Pending</h3>
//                         : ''
//                     }
//                     {currentRound.stopType === RoundStopTypeEnum.SOLD
//                         ? (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE || currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN)
//                             ? ((currentRoundBidding.choiceLotsSelection && currentRoundBidding.choiceLotsSelection.length)
//                                     ? <h3 className="display-2">Sold</h3>
//                                     : ''
//                             )
//
//                             : <h3 className="display-2">Sold</h3>
//                         : ''
//                     }
//                 </div>
//             </div>
//         )
//     }
//
//
//     return <h1 className="display-1 text-left">Waiting for bidding to start</h1>
//
//
// }
//
// // React.memo acts as a PureComponent here (https://reactjs.org/blog/2018/10/23/react-v-16-6.html#reactmemo)
// const LotListItem = React.memo(function LotListItemMemoCmp(props: LotListItemPropsType) {
//     const {index, style} = props;
//     const itemData: LotListItemDataType = props.data;
//     const closedRounds = itemData.closedRounds;
//     const lotsMapById = itemData.lotsMapById;
//     const round: Round = closedRounds[index];
//     const itemsQty = round.lotsIds.map(lotId => lotsMapById.get(lotId).itemsQty);
//     const totalPrice = round.highestBid ? round.highestBid.value * itemsQty[0] : null;
//
//     return (
//         <div className="round-list__item" style={style}>
//             <p className="mb-0 fw-bold">{ROUND_TYPES_DISPLAY_NAMES_MAP.get(round.roundType)}: {round.lotsIds.map(lotId => `#${lotsMapById.get(lotId).number}`).join(", ")}</p>
//             {round.stopType !== RoundStopTypeEnum.NO_SALE ?
//                 <p>
//                     <strong>{ROUND_STOP_TYPES_MAP.get(round.stopType)}</strong> for <strong><NumberDisplay
//                     valueCents={round.highestBid.value} allowZero={false}/>
//                     {round.roundType === RoundTypeEnum.GROUP_CHOICE ||
//                     round.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN ||
//                     round.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY ?
//                         ' each'
//                         :
//                         ''
//                     }
//                     {round.roundType === RoundTypeEnum.SINGLE_BOXLOT && itemsQty > 1 ?
//                         (<span> X {itemsQty} = <NumberDisplay valueCents={totalPrice} allowZero={false}/></span>)
//                         :
//                         ''
//                     }
//                 </strong> to <span
//                     className="mb-0 text-danger fw-bold">{round.highestBid.bidderNo === '0' ? 'FLOOR' : `${round.highestBid.bidderNo} (${round.highestBid.bidderLabel})`}</span>
//                 </p>
//                 :
//                 <p className="text-danger fw-bold">NO SALE</p>
//             }
//         </div>
//     )
// }, areEqual);
//
//
// const createLotsListItemData = memoizeOne((closedRounds, lotsMapById) => ({
//     closedRounds,
//     lotsMapById
// }));
//
// class AuctioneerRootCmp extends React.PureComponent<RootCmpPropsType, RootCmpStateType> {
//
//     constructor(props) {
//         super(props);
//         _logger.debug('.constructor()', props);
//
//         this.state = {
//             connectInProgress: true,
//             currentIndex: 0
//         }
//     }
//
//     componentDidMount(): void {
//         _logger.debug('.componentDidMount()');
//
//         this.setState({
//             connectInProgress: true
//         });
//
//         AuctionsManager.currentWebcastAuctionHandler.connect()
//             .then(() => {
//                 console.log('.componentDidMount()>currentWebcastAuctionHandler.connect().then()');
//                 this.setState({
//                     connectInProgress: false
//                 });
//             })
//             .catch((reason) => {
//                 console.error('.componentDidMount()>currentWebcastAuctionHandler.connect().catch()', reason);
//                 this.setState({
//                     connectInProgress: false
//                 });
//             });
//
//     }
//
//     _renderAppLoadingState() {
//         return (
//             <div className="w-100 h-100 d-flex justify-content-center align-items-left">
//                 <h3 className="display-2">Loading, please wait ...</h3>
//             </div>
//         )
//     }
//
//
//     _renderLoadedAppState() {
//         const {webcastAuctionData, currentRound, currentRoundBidding, highestBid} = this.props;
//         const lotsMapById = webcastAuctionData.lotsMapById;
//         const lots = currentRound.lotsIds.map(lotId => lotsMapById.get(lotId));
//         const closedRounds = webcastAuctionData.closedRounds;
//         const highestBidParticipant:Participant = this.props.highestBid && this.props.participantsByUserId.get(this.props.highestBid.userId);
//         const highestBidParticipantPersonName:?string = highestBidParticipant ? highestBidParticipant.bidderPersonName : null;
//
//         let mostRecentClosedRound = closedRounds.map((closedRound, index) => ({
//             ...closedRound,
//             index
//         })).sort((a, b) => a.index > b.index ? -1 : 1);
//
//         const itemData = createLotsListItemData(mostRecentClosedRound, lotsMapById);
//
//         return (
//             <div className="d-flex flex-column flex-grow-1">
//                 <div className={`auctioneer-content-wrapper row mb-3 mx-1 py-2 flex-grow-1 ${highestBid && highestBid.bidderDisplay !== "FLOOR" ? 'flash' : ''}`}>
//                     <div className="col-3">
//                         <div className="d-flex align-content-around flex-column">
//                             <div className="mb-5">
//                                 {lots[0].images && lots[0].images.length > 0 && <img className="img-fluid" src={lots[0].images[0].mdUrl}/> }
//                             </div>
//                         </div>
//                     </div>
//                     <div className="col-8 offset-1">
//                         <div className="d-flex flex-column flex-grow-1">
//                             <p className="h3 text-right">Increment: {currentRoundBidding.bidIncrement
//                                 ? <CurrencyAmountDisplay valueCents={currentRoundBidding.bidIncrement}/>
//                                 : '0'
//                             }
//                             </p>
//                             <BidDetails currentRound={currentRound} currentRoundBidding={currentRoundBidding} bidderPersonName={highestBidParticipantPersonName} />
//                         </div>
//                     </div>
//                     <div className="col-6 d-flex">
//                         <div className="card mt-1 flex-grow-1">
//                             <div className="card-header bg-light">
//                                 Bidding on
//                             </div>
//                             <div className="card-body position-relative overflow-auto">
//                                 <div className="row">
//                                     <div className="col-7">
//                                         <p className="h3">{ROUND_TYPES_DISPLAY_NAMES_MAP.get(currentRound.roundType)}</p>
//                                         <p className="ps-2 h5">{lots.map(lot => <span key={lot.id}>Lot #{lot.number} <br/></span>)}</p>
//                                         <p className="ps-2 h4">{lots.map(lot => <span key={lot.id}>{lot.name} <br/></span>)}</p>
//                                         <p className="ps-2 text-truncate-two">{lots.map(lot => <span key={lot.id}>{lot.description} <br/></span>)}</p>
//                                     </div>
//                                     <div className="col-5">
//                                         <ClerkCurrentRoundPrebidsCmp />
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                     <div className="col-6 d-flex">
//                         <div className="card mt-1 flex-grow-1">
//                             <div className="card-header bg-light">
//                                 Closed Lots
//                             </div>
//                             <div className="card-body d-flex">
//                                 <div className="d-flex flex-grow-1">
//                                     <div className="d-flex flex-grow-1">
//                                         <AutoSizer>
//                                             {({height, width}) => (
//                                                 <FixedSizeList
//                                                     className="auctioneer-lots-list__list-container"
//                                                     height={height}
//                                                     width={width}
//                                                     itemCount={closedRounds.length}
//                                                     itemSize={55}
//                                                     itemData={itemData}
//                                                     itemKey={(index, itemData) => (itemData.closedRounds[index].roundId)}
//                                                 >
//                                                     {LotListItem}
//                                                 </FixedSizeList>
//                                             )}
//                                         </AutoSizer>
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         )
//     }
//
//
//     _renderEventClosedAppState() {
//         return (
//             <div className="w-100 h-100 d-flex justify-content-center align-items-center">
//                 <h3 className="display-2">The webcast event has closed. Thank you.</h3>
//             </div>
//         )
//     }
//
//
//     _renderAppState() {
//         if (this.props.webcastStatusState === WebcastStateEnum.WEBCAST_STATE__CLOSED) {
//             return this._renderEventClosedAppState();
//         }
//
//         if (this.props.webcastStatusState > WebcastStateEnum.WEBCAST_STATE__LOADING) {
//             return this._renderLoadedAppState();
//         }
//
//         return this._renderAppLoadingState();
//     }
//
//
//     render() {
//
//         return <React.Fragment>
//             <NetworkDisconnectedAlertPanelOverlayCmp
//                 connectInProgress={this.state.connectInProgress}
//             />
//
//             {this._renderAppState()}
//
//             <WebcastPageFooterLineCmp screenName={'Auctioneer'}/>
//
//         </React.Fragment>
//
//     }
// }
//
// export default connect(({activeWebcast}: { activeWebcast: ReduxActiveWebcastSubStateType }) => {
//     return {
//         webcastAuctionData: activeWebcast.webcastAuctionData,
//         currentRound: activeWebcast.webcastCurrentRound,
//         currentRoundBidding: activeWebcast.webcastCurrentRoundBidding,
//         webcastStatusState: activeWebcast.webcastStatus.state,
//         highestBid: activeWebcast.webcastCurrentRoundBidding.highestBid,
//         participantsByUserId: activeWebcast.webcastParticipants.mapByUserId,
//     }
// })(AuctioneerRootCmp);
